/**
 * @file SetUserCredentialsForm.js
 * @description This file contains the SetUserCredentialsForm component, which is responsible for rendering a form to set user credentials and submit the onboarding process.
 */
import React, { useState } from 'react';
import Toast from '../ui/toasts/Toast';
import {
    onboardUser,
    submitSelfRegistration,
} from '../../services/userService';
import PasswordInput from '../ui/PasswordInput';
import { useAuthContext } from '../../store/Contexts';
import { hasSequentialChars } from '../../utils/utils';
import { getText } from '../../i18n';

const SetUserCredentialsForm = ({
    onSetCredentials,
    onboardingInfo,
    notificationPreferences,
}) => {
    const { companyCode, onboardingCode, isSelfRegistration, profileData } =
        onboardingInfo;
    const [credentials, setCredentials] = useState({
        password: '',
        confirmPassword: '',
    });

    const { companyConfig } = useAuthContext();
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [passwordRequirements, setPasswordRequirements] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
        repeats: false,
        sequential: false,
        patterns: false,
    });

    const displayUsername = isSelfRegistration
        ? notificationPreferences?.username || profileData?.email
        : notificationPreferences?.username;

    const handleChange = event => {
        const { name, value } = event.target;
        setCredentials(prevCredentials => ({
            ...prevCredentials,
            [name]: value,
        }));

        if (name === 'password') {
            validatePassword(value);
        }

        if (errors[name]) {
            setErrors(prevErrors => ({ ...prevErrors, [name]: null }));
        }
    };

    const validatePassword = password => {
        setPasswordRequirements({
            length:
                password.length >=
                    companyConfig.application?.password_min_length &&
                password.length <=
                    companyConfig.application?.password_max_length,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /\d/.test(password),
            special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
            repeats: !/(.)\1{3}/.test(password),
            sequential: !hasSequentialChars(password),
            patterns: !/(.{1,2})\1\1/.test(password),
        });
    };

    const handleSubmit = async event => {
        event.preventDefault();
        const newErrors = validateForm(credentials);

        if (Object.keys(newErrors).length === 0) {
            setIsSubmitting(true);
            try {
                if (isSelfRegistration) {
                    const response = await submitSelfRegistration(
                        profileData,
                        credentials.password,
                        { communicationPreferences: notificationPreferences },
                        companyCode,
                    );
                    onSetCredentials({
                        username: displayUsername,
                        password: credentials.password,
                        ...response,
                    });
                } else {
                    const response = await onboardUser(
                        `${onboardingCode}`,
                        companyCode,
                        notificationPreferences.username,
                        credentials.password,
                        notificationPreferences.mobilePhoneNumber,
                        notificationPreferences.email,
                        notificationPreferences.comm_preference,
                        notificationPreferences.notification_preferred_method_id,
                    );
                    onSetCredentials(response);
                }
            } catch (error) {
                const errorDetail =
                    (error.response && error.response.data.detail) ||
                    'An error occurred during registration';
                Toast.error(errorDetail);
            } finally {
                setIsSubmitting(false);
            }
        } else {
            setErrors(newErrors);
            const errorMessages = Object.values(newErrors).join(', ');
            Toast.error(errorMessages);
        }
    };

    const validateForm = ({ password, confirmPassword }) => {
        const newErrors = {};
        if (!password) newErrors.password = 'Password is required';
        if (password !== confirmPassword)
            newErrors.confirmPassword = 'Passwords do not match';

        const unmetRequirements = Object.entries(passwordRequirements)
            .filter(([, met]) => !met)
            .map(([req]) => req);

        if (unmetRequirements.length > 0) {
            newErrors.password = `Your password didn't meet the guidelines set by your administrator. Please input a new password`;
        }

        return newErrors;
    };

    const getLabelClassName = fieldName =>
        errors[fieldName] ? 'text-danger' : '';

    const renderPasswordRequirement = (requirement, text) => {
        const isMet = passwordRequirements[requirement];
        return (
            <small
                className={`password-requirement d-block w-100 ${
                    !credentials.password
                        ? 'text-muted'
                        : isMet
                          ? 'text-success'
                          : 'text-danger'
                }`}
            >
                {credentials.password && (
                    <i className={`bi ${isMet ? 'bi-check' : 'bi-x'} me-2`}></i>
                )}
                {text}
            </small>
        );
    };

    return (
        <div className="form-container p-3">
            <h2 className="iNGAGED-Typography-Heading3 mb-4">{getText('msg_b365567e6b78')}</h2>

            {displayUsername && (
                <div className="form-group mb-4">
                    <label className="text-muted">{getText('msg_84c29015de33')}</label>
                    <div className="form-control-plaintext fw-bold">
                        {displayUsername}
                    </div>
                </div>
            )}

            <form
                className="needs-validation text-left"
                onSubmit={handleSubmit}
                noValidate
            >
                <div className="form-group mb-4">
                    <label
                        htmlFor="password"
                        className={getLabelClassName('password')}
                    >
                        {getText('msg_8be3c943b160')}
                    </label>
                    <PasswordInput
                        id="password"
                        name="password"
                        value={credentials.password}
                        onChange={handleChange}
                        placeholder={getText('msg_8be3c943b160')}
                        errorMessage={!!errors.password}
                    />
                    <div className="password-requirements mt-2">
                        {renderPasswordRequirement(
                            'length',
                            `Between ${companyConfig.application?.password_min_length}-${companyConfig.application?.password_max_length} characters`,
                        )}
                        {renderPasswordRequirement(
                            'uppercase',
                            'Contains at least 1 uppercase letter',
                        )}
                        {renderPasswordRequirement(
                            'lowercase',
                            'Contains at least 1 lowercase letter',
                        )}
                        {renderPasswordRequirement(
                            'number',
                            'Contains at least 1 number',
                        )}
                        {renderPasswordRequirement(
                            'special',
                            'Contains at least 1 special character',
                        )}
                        {companyConfig.application
                            ?.password_allow_duplicate_values ||
                            renderPasswordRequirement(
                                'repeats',
                                'No 4 consecutive repeating characters',
                            )}
                        {companyConfig.application
                            ?.password_allow_duplicate_values ||
                            renderPasswordRequirement(
                                'sequential',
                                'No 4 sequential characters',
                            )}
                        {companyConfig.application
                            ?.password_allow_duplicate_values ||
                            renderPasswordRequirement(
                                'patterns',
                                'No repeating patterns',
                            )}
                    </div>
                </div>

                <div className="form-group mb-3">
                    <label
                        htmlFor="confirmPassword"
                        className={getLabelClassName('confirmPassword')}
                    >
                        {getText('msg_c2d404cb7bad')}
                    </label>
                    <PasswordInput
                        id="confirmPassword"
                        name="confirmPassword"
                        value={credentials.confirmPassword}
                        onChange={handleChange}
                        placeholder={getText('msg_c2d404cb7bad')}
                        errorMessage={errors.confirmPassword}
                        isConfirmField={true}
                        originalPassword={credentials.password}
                        showMatchFeedback={true}
                    />
                </div>

                <div className="form-group d-grid gap-2 col-4 mx-auto text-center pt-4 pb-2">
                    <button
                        type="submit"
                        className="ing-button-primary-left"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            'Saving...'
                        ) : (
                            <>
                                {getText('msg_bc981983e7f5')}
                                <i className="bi bi-chevron-right"></i>
                            </>
                        )}
                    </button>
                </div>
                <div className="form-group d-grid gap-2 col-10 mx-auto text-center pt-3">
                    <small>
                        <a
                            href={`/get-support${companyCode ? `?companyCode=${companyCode}` : ''}`}
                            className="pl-2"
                        >
                            {getText('msg_97e2e9fd5798')}
                        </a>
                    </small>
                </div>
            </form>
        </div>
    );
};

export default SetUserCredentialsForm;
